/**
 * MarketStatistic Component
 *
 * Displays market average information for comparison purposes.
 * Shows users how their values compare to others in their age group.
 */

import React from "react";

export interface MarketStatisticProps {
  /** Label describing what this statistic is for */
  label: string;
  /** Market average value */
  marketAverage: number;
  /** Whether to format as currency (default: true) */
  currency?: boolean;
}

/**
 * MarketStatistic - Display market average information
 *
 * Shows a small, unobtrusive card with market average data for context.
 * Helps users understand typical values for their age group.
 *
 * @param props - Component props
 * @returns Market statistic display
 */
export const MarketStatistic: React.FC<MarketStatisticProps> = ({
  label,
  marketAverage,
  currency = true,
}) => {
  const formattedValue = currency
    ? `$${marketAverage.toLocaleString()}`
    : marketAverage.toLocaleString();

  return (
    <div
      className="market-statistic-container"
      id={`statistic-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="market-statistic-content">
        <div className="market-statistic-icon">
          <svg
            className="market-statistic-icon-svg"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <div className="market-statistic-text">
          <p className="statistic-label">{label}</p>
          <p className="statistic-value">
            Age average:{" "}
            <span className="statistic-value-bold">{formattedValue}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MarketStatistic;
